import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, unref as _unref, mergeProps as _mergeProps, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "wrapper" }
const _hoisted_2 = { class: "contents-list-icon" }
const _hoisted_3 = {
  key: 0,
  class: "contents-list-icon-main"
}
const _hoisted_4 = { class: "contents-question-text" }
const _hoisted_5 = { class: "attention" }
const _hoisted_6 = { class: "contents-question-confirm-button" }

import { onMounted, reactive, ref } from "vue";
import { processend, processing } from "@/utils/view";
import { SurveyFormCollection } from "@/models/healthCare/surveyFormDetail";
import QuestionTable from "@/components/modules/healthCare/QuestionTable.vue";
import { Question } from "@/models/healthCare/types/question";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import ErrorDialog from "@/views/base/CommonMessage.vue";
import { _$t } from "@/utils/i18n";
import Footer from "@/components/modules/base/Footer.vue";

// ---- Constant ----------------------------------------------
const componentId = "generalpurpose03-02-a";
interface Survey {
  survey_code: string;
  disease_code: string;
  survey_classification_code: string;
  survey_title: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SurveyForm',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const store = useStore();
const questionData = ref<Question[]>([]);

// ---- Variable ----------------------------------------------
let surveyCode = "";
let surveyObject: Survey | null = null;
// セッションエラーチェック
let sessionErr = ref(false);
// 設問が未登録チェック
let questionEmptyFlg = ref(false);

//アンケートの回答期限・設問未登録・セッションエラーチェック
let errorDialog = reactive({
  show: false,
  okButtonHide: true,
  okName: _$t("survey_msg_ok_btn"),
  safe: true,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialog.message = message;
    errorDialog.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialog.show = false;
    errorDialog.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    } else if (questionEmptyFlg.value) {
      cancel();
    }
  },
});

// ---- Method ----------------------------------------------
//アンケート情報取得
const fetchSurvey = async () => {
  const surveyForm = new SurveyFormCollection();
  const [_collection, response, err] = await surveyForm.getSurvey(surveyCode);
  if (err) {
    const status = err.error.response.status;
    if (status === 404) {
      window.location.href = "/404";
    } else if (status === 400) {
      // アンケートの回答期限エラー
      router.push({
        name: "survey/expired",
      });
      throw err;
    } else {
      const decodedMessage = decodeURIComponent(
        err.error?.response?.data?.message
      );
      // セッションエラー
      if (status === 408) sessionErr.value = true;
      errorDialog.open(decodedMessage);
      return;
    }
  }
  if (response?.data?.result) {
    surveyObject = response.data.result;
  }
};

// アンケート設問取得
const fetchQuestions = async () => {
  const surveyForm = new SurveyFormCollection();
  const [_collection, response, err] = await surveyForm.getQuestions(
    surveyCode
  );
  if (err) {
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    const status = err.error?.response?.status;
    if (status === 400) {
      // 設問が未登録
      questionEmptyFlg.value = true;
      errorDialog.okName = _$t("survey_msg_back_btn");
    } else if (status === 408) {
      // セッションエラー
      sessionErr.value = true;
    }
    errorDialog.open(decodedMessage);
    return;
  }
  if (response?.data?.result.length > 0) {
    questionData.value = response.data.result.map((q: any) => ({
      survey_code: q.survey_code,
      disease_code: surveyObject!.disease_code,
      question_code: q.question_code,
      survey_classification_code: surveyObject!.survey_classification_code,
      survey_title: surveyObject!.survey_title,
      sort: q.sort,
      question_text: q.question_text,
      required: q.required,
      answer_format: q.answer_format,
      choices:
        q.choices?.map((choice: any) => ({
          choice_code: choice.choice_code,
          choice_text: choice.choice_text,
        })) || undefined,
      answer: q.answer_format === 3 ? [] : "",
      otherAnswer: q.answer_format === 3 ? "" : undefined,
      error: q.required ? "" : undefined,
      preview: q.answer_format === 4 ? "" : undefined,
    }));
  }
};

//回答提出
const submitAnswers = () => {
  let isValid = true;
  // check require
  questionData.value.forEach((question: any) => {
    question.error = "";
    if (
      question.required &&
      (!question.answer ||
        (Array.isArray(question.answer) && question.answer.length === 0))
    ) {
      question.error = _$t("survey_msg_require_error");
      isValid = false;
      return { ...question };
    }
  });
  if (isValid) {
    store.commit("SET_QUESTIONS", {
      questions: questionData.value,
      sessionId: surveyCode,
    });
    router.push({
      name: "health-care/survey/confirm",
    });
  }
};

// キャンセル処理
const cancel = () => {
  const diseaseCode =
    surveyObject?.disease_code || questionData.value[0]?.disease_code;
  if (diseaseCode) {
    router.push({
      name: "health-care/survey",
      params: { diseaseCode },
    });
  } else {
    router.push({ path: "/home" });
  }
};
// ---- Vue lifeCycle ----------------------------------------------
onMounted(async () => {
  surveyCode = route.params.surveyCode as string;
  let storedQuestions = [];
  if (surveyCode) {
    storedQuestions = store.getters.getQuestions({ sessionId: surveyCode });
  }

  if (storedQuestions.length > 0) {
    questionData.value = storedQuestions;
  } else {
    await fetchSurvey();
    await fetchQuestions();
  }
  document.title = questionData.value[0]?.survey_title || "";
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", { id: componentId }, [
      _createElementVNode("div", _hoisted_1, [
        _cache[3] || (_cache[3] = _createElementVNode("header", null, [
          _createElementVNode("h1", { class: "header-icon" }, [
            _createElementVNode("a", { href: "#" }, [
              _createElementVNode("img", { src: "/server/necs/images/kanshin_logo.png" })
            ])
          ])
        ], -1)),
        _createElementVNode("main", null, [
          _createElementVNode("div", _hoisted_2, [
            (questionData.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(questionData.value[0].survey_title), 1))
              : _createCommentVNode("", true),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "contents-list-icon-tri" }, null, -1))
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createTextVNode(_toDisplayString(_ctx.$t("survey_msg_please_button_confirm")), 1),
            _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1)),
            _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("survey_msg_please_input")), 1)
          ]),
          _createVNode(QuestionTable, {
            questions: questionData.value,
            "onUpdate:questions": _cache[0] || (_cache[0] = ($event: any) => ((questionData).value = $event))
          }, null, 8, ["questions"]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              class: "cancel-button",
              onClick: cancel
            }, _toDisplayString(_ctx.$t("survey_button_cancel")), 1),
            _createElementVNode("button", {
              class: "confirm-button",
              onClick: submitAnswers
            }, _toDisplayString(_ctx.$t("survey_button_confirm")), 1)
          ])
        ])
      ])
    ]),
    _createVNode(Footer, { "scroll-target": componentId }),
    (_unref(errorDialog).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 0 }, _unref(errorDialog), {
          onOk: _unref(errorDialog).close,
          onCancel: _unref(errorDialog).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})